import { useState, useEffect, useRef } from 'react';

interface RemixModalProps {
  originalPrompt: string;
  style: string;
  duration: string;
  resolution: string;
  darkMode: boolean;
  onClose: () => void;
  onRemix: (newPrompt: string) => void;
}

interface VariationSuggestion {
  id: string;
  label: string;
  icon: string;
  prompt: string;
  category: string;
}

const STYLE_SWAPS = ['Cinematic', 'Animated', 'Documentary', 'Surrealist', 'Noir', 'Retro 8mm'];
const MOOD_OPTIONS = ['Melancholic', 'Euphoric', 'Tense', 'Dreamy', 'Gritty', 'Serene'];
const CAMERA_OPTIONS = ['Overhead drone shot', 'Extreme close-up', 'Dutch angle tilt', 'POV perspective', 'Tracking shot', 'Wide establishing shot'];
const TIME_OPTIONS = ['Golden hour', 'Midnight blue hour', 'Harsh midday sun', 'Storm-lit dusk', 'Neon night rain'];
const PACE_OPTIONS = ['Fast-cut montage', 'Slow motion 240fps', 'Time-lapse', 'Real-time uncut', 'Hyperlapse'];

function buildVariations(prompt: string): VariationSuggestion[] {
  const pick = <T,>(arr: T[]) => arr[Math.floor(Math.random() * arr.length)];
  return [
    {
      id: 'style',
      label: 'Swap visual style',
      icon: 'ri-brush-line',
      prompt: `${prompt} — rendered in a ${pick(STYLE_SWAPS)} visual style with distinct aesthetic choices`,
      category: 'Style',
    },
    {
      id: 'mood',
      label: 'Shift the mood',
      icon: 'ri-emotion-line',
      prompt: `${prompt} — with a ${pick(MOOD_OPTIONS).toLowerCase()} emotional tone, color palette and score to match`,
      category: 'Mood',
    },
    {
      id: 'camera',
      label: 'Change camera angle',
      icon: 'ri-camera-lens-line',
      prompt: `${prompt} — framed as a ${pick(CAMERA_OPTIONS).toLowerCase()}, emphasizing depth and spatial drama`,
      category: 'Camera',
    },
    {
      id: 'time',
      label: 'Change time of day',
      icon: 'ri-sun-cloudy-line',
      prompt: `${prompt} — set during ${pick(TIME_OPTIONS).toLowerCase()}, light and atmosphere changed accordingly`,
      category: 'Lighting',
    },
    {
      id: 'pace',
      label: 'Alter pacing',
      icon: 'ri-speed-line',
      prompt: `${prompt} — edited as a ${pick(PACE_OPTIONS).toLowerCase()} for maximum impact and viewer engagement`,
      category: 'Pacing',
    },
    {
      id: 'invert',
      label: 'Flip the narrative',
      icon: 'ri-flip-horizontal-line',
      prompt: `The reverse of: ${prompt}. Tell the story from the opposite perspective or ending back to beginning`,
      category: 'Narrative',
    },
  ];
}

const TWEAK_PRESETS = [
  { icon: 'ri-flashlight-line', label: 'More dramatic', append: ', heightened drama, intense emotional stakes' },
  { icon: 'ri-leaf-line', label: 'More serene', append: ', peaceful atmosphere, gentle pacing, tranquil soundtrack' },
  { icon: 'ri-contrast-line', label: 'High contrast', append: ', bold high-contrast visuals, strong chiaroscuro lighting' },
  { icon: 'ri-palette-line', label: 'Vibrant colors', append: ', super-saturated vivid color palette, pop art energy' },
  { icon: 'ri-scissors-line', label: 'Shorter / snappier', append: ', tight editing, punchy cuts, no dead air' },
  { icon: 'ri-mic-line', label: 'Add narration', append: ', professional voiceover narration throughout, conversational tone' },
];

const BADGE_COLORS: Record<string, string> = {
  Style: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
  Mood: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300',
  Camera: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  Lighting: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
  Pacing: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  Narrative: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
};

export default function RemixModal({ originalPrompt, style, duration, resolution, darkMode, onClose, onRemix }: RemixModalProps) {
  const [remixPrompt, setRemixPrompt] = useState(originalPrompt);
  const [activeVariation, setActiveVariation] = useState<string | null>(null);
  const [variations] = useState<VariationSuggestion[]>(() => buildVariations(originalPrompt));
  const [isGeneratingSuggestions, setIsGeneratingSuggestions] = useState(false);
  const [aISuggestions, setAISuggestions] = useState<string[]>([]);
  const [showAISuggestions, setShowAISuggestions] = useState(false);
  const [diffView, setDiffView] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    textareaRef.current?.focus();
  }, []);

  const handleSelectVariation = (v: VariationSuggestion) => {
    setActiveVariation(v.id);
    setRemixPrompt(v.prompt);
    setDiffView(true);
  };

  const handleTweakPreset = (append: string) => {
    setRemixPrompt(prev => {
      const base = prev.trim().replace(/,\s*$/, '');
      return `${base}${append}`;
    });
    setActiveVariation(null);
  };

  const handleAISuggest = async () => {
    setIsGeneratingSuggestions(true);
    await new Promise(r => setTimeout(r, 1800));
    const words = originalPrompt.split(' ').slice(0, 8).join(' ');
    setAISuggestions([
      `${words} — seen through the eyes of the last surviving witness, intimate handheld footage, raw emotion`,
      `${words} — reimagined in 1970s retro aesthetic, film grain, analogue color bleed, lo-fi warmth`,
      `${words} — epic widescreen spectacle with sweeping orchestral score, helicopter aerials, ultra-cinematic`,
    ]);
    setShowAISuggestions(true);
    setIsGeneratingSuggestions(false);
  };

  const handleGenerate = () => {
    if (!remixPrompt.trim()) return;
    onRemix(remixPrompt);
    onClose();
  };

  const isChanged = remixPrompt.trim() !== originalPrompt.trim();

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}></div>
      <div className={`relative w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-2xl shadow-2xl ${
        darkMode ? 'bg-gray-900 border border-gray-700' : 'bg-white border border-gray-200'
      }`}>
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-red-500 to-orange-600 rounded-t-2xl">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
              <i className="ri-refresh-line text-white text-base"></i>
            </div>
            <div>
              <h2 className="text-white font-bold text-base">Remix This Video</h2>
              <p className="text-white/70 text-xs">Tweak the prompt and regenerate a variation</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-lg transition-colors">
            <i className="ri-close-line text-white text-lg"></i>
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Original vs Remix toggle */}
          {isChanged && (
            <div className={`flex items-center gap-2 p-3 rounded-xl text-xs ${
              darkMode ? 'bg-green-900/20 border border-green-800 text-green-400' : 'bg-green-50 border border-green-200 text-green-700'
            }`}>
              <i className="ri-git-branch-line text-sm flex-shrink-0"></i>
              <span className="flex-1">Prompt modified — {remixPrompt.length} chars</span>
              <button
                onClick={() => setDiffView(v => !v)}
                className={`px-2 py-1 rounded-lg font-semibold transition-colors ${
                  darkMode ? 'bg-green-800/50 hover:bg-green-800 text-green-300' : 'bg-green-100 hover:bg-green-200 text-green-700'
                }`}
              >
                {diffView ? 'Hide original' : 'Show original'}
              </button>
              <button
                onClick={() => { setRemixPrompt(originalPrompt); setActiveVariation(null); setDiffView(false); }}
                className={`px-2 py-1 rounded-lg font-semibold transition-colors ${
                  darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'
                }`}
              >
                Reset
              </button>
            </div>
          )}

          {diffView && isChanged && (
            <div className={`rounded-xl p-3 text-xs space-y-1.5 border ${
              darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
            }`}>
              <p className={`font-semibold ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                <i className="ri-history-line mr-1"></i>Original
              </p>
              <p className={`leading-relaxed ${darkMode ? 'text-gray-400' : 'text-gray-500'} line-clamp-3`}>{originalPrompt}</p>
            </div>
          )}

          {/* Prompt Textarea */}
          <div>
            <label className={`block text-sm font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              <i className="ri-edit-line mr-2 text-red-500"></i>
              Your Remixed Prompt
            </label>
            <textarea
              ref={textareaRef}
              value={remixPrompt}
              onChange={(e) => { setRemixPrompt(e.target.value.slice(0, 1200)); setActiveVariation(null); }}
              className={`w-full h-36 rounded-xl border px-4 py-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-red-500 ${
                darkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-400'
              }`}
              placeholder="Describe your remixed video..."
            />
            <div className="flex justify-between mt-1.5">
              <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{remixPrompt.length} / 1200</span>
              <button
                onClick={handleAISuggest}
                disabled={isGeneratingSuggestions}
                className={`flex items-center gap-1.5 text-xs font-semibold transition-colors ${
                  isGeneratingSuggestions ? 'text-amber-500 cursor-wait' : 'text-amber-500 hover:text-amber-600'
                }`}
              >
                {isGeneratingSuggestions
                  ? <><i className="ri-loader-4-line animate-spin"></i>Getting AI suggestions…</>
                  : <><i className="ri-sparkling-line"></i>Get AI suggestions</>
                }
              </button>
            </div>
          </div>

          {/* AI Suggestions */}
          {showAISuggestions && aISuggestions.length > 0 && (
            <div className={`rounded-xl border p-4 space-y-2 ${
              darkMode ? 'bg-amber-900/10 border-amber-800' : 'bg-amber-50 border-amber-200'
            }`}>
              <p className={`text-xs font-semibold flex items-center gap-1.5 ${darkMode ? 'text-amber-400' : 'text-amber-700'}`}>
                <i className="ri-sparkling-line"></i>AI Remix Suggestions — click to load
              </p>
              {aISuggestions.map((s, i) => (
                <button
                  key={i}
                  onClick={() => { setRemixPrompt(s); setShowAISuggestions(false); setDiffView(true); }}
                  className={`w-full text-left p-3 rounded-lg text-xs leading-relaxed transition-all border ${
                    darkMode
                      ? 'bg-gray-800 border-gray-700 hover:border-amber-700 text-gray-300 hover:text-amber-300'
                      : 'bg-white border-gray-200 hover:border-amber-400 text-gray-700 hover:text-amber-700'
                  }`}
                >
                  <span className={`font-bold mr-2 ${darkMode ? 'text-amber-400' : 'text-amber-600'}`}>{i + 1}.</span>
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Quick Tweak Presets */}
          <div>
            <p className={`text-xs font-semibold mb-2.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <i className="ri-magic-line mr-1 text-red-500"></i>Quick tweaks — appends to your prompt
            </p>
            <div className="flex flex-wrap gap-2">
              {TWEAK_PRESETS.map(p => (
                <button
                  key={p.label}
                  onClick={() => handleTweakPreset(p.append)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                    darkMode
                      ? 'bg-gray-800 border-gray-600 text-gray-300 hover:bg-gray-700 hover:border-red-600 hover:text-red-300'
                      : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-red-50 hover:border-red-300 hover:text-red-700'
                  }`}
                >
                  <i className={`${p.icon} text-sm`}></i>
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Variation Templates */}
          <div>
            <p className={`text-xs font-semibold mb-2.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <i className="ri-layout-grid-line mr-1 text-red-500"></i>Variation templates — rewrites the prompt
            </p>
            <div className="grid grid-cols-2 gap-2">
              {variations.map(v => (
                <button
                  key={v.id}
                  onClick={() => handleSelectVariation(v)}
                  className={`flex items-start gap-3 p-3 rounded-xl border-2 transition-all text-left ${
                    activeVariation === v.id
                      ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                      : darkMode
                      ? 'border-gray-700 bg-gray-800 hover:border-gray-600'
                      : 'border-gray-200 bg-gray-50 hover:border-red-300 hover:bg-red-50/50'
                  }`}
                >
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    activeVariation === v.id ? 'bg-red-500 text-white' : darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'
                  }`}>
                    <i className={`${v.icon} text-sm`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <span className={`text-xs font-semibold ${activeVariation === v.id ? 'text-red-600 dark:text-red-400' : darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {v.label}
                      </span>
                    </div>
                    <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded-full font-medium ${BADGE_COLORS[v.category]}`}>
                      {v.category}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Settings preview strip */}
          <div className={`rounded-xl p-3 border flex flex-wrap gap-2 items-center ${
            darkMode ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'
          }`}>
            <span className={`text-xs font-semibold mr-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Keeping settings:
            </span>
            {[style, duration, resolution].map(tag => (
              <span key={tag} className={`text-[11px] px-2.5 py-1 rounded-full font-medium ${
                darkMode ? 'bg-gray-700 text-gray-300' : 'bg-white border border-gray-200 text-gray-600'
              }`}>{tag}</span>
            ))}
            <span className={`ml-auto text-[10px] ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              <i className="ri-information-line mr-0.5"></i>Tweak in Advanced Settings if needed
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className={`sticky bottom-0 px-6 py-4 border-t flex gap-3 ${
          darkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-100'
        }`}>
          <button
            onClick={onClose}
            className={`px-5 py-3 rounded-xl font-semibold text-sm transition-all ${
              darkMode ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Cancel
          </button>
          <button
            onClick={handleGenerate}
            disabled={!remixPrompt.trim()}
            className="flex-1 py-3 bg-gradient-to-r from-red-500 to-orange-600 text-white font-bold rounded-xl hover:shadow-lg hover:shadow-red-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
          >
            <i className="ri-refresh-line text-base"></i>
            Generate Remix
            {isChanged && <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">Modified</span>}
          </button>
        </div>
      </div>
    </div>
  );
}
